
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { KeyRound, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidRecovery, setIsValidRecovery] = useState(false);

  useEffect(() => {
    const validateRecoveryToken = async () => {
      try {
        // Get the current session and check if it's in recovery mode
        const { data: { session } } = await supabase.auth.getSession();
        console.log("Current session:", session);
        
        // Check URL hash for recovery type
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get('type');
        console.log("Recovery type from URL:", type);

        if (type === 'recovery') {
          console.log("Valid recovery flow detected");
          setIsValidRecovery(true);
        } else if (session) {
          // If we have a session but not in recovery mode, still allow password update
          console.log("Session exists but not in recovery mode");
          setIsValidRecovery(true);
        } else {
          console.error("Invalid or missing recovery token");
          toast.error("Invalid password reset link");
          navigate("/auth");
        }
      } catch (error) {
        console.error("Error validating recovery token:", error);
        toast.error("Error validating reset link");
        navigate("/auth");
      }
    };

    validateRecoveryToken();
  }, [navigate]);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidRecovery) {
      toast.error("Invalid recovery session");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);

    try {
      console.log("Password reset: Updating password...");
      
      // Get current session info before password update
      const { data: { session: beforeSession } } = await supabase.auth.getSession();
      const userEmail = beforeSession?.user?.email;
      
      if (!userEmail) {
        throw new Error("No user email found in session");
      }

      console.log("Password reset: Found user email:", userEmail);

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      console.log("Password reset: Password updated successfully, now verifying...");
      
      // CRITICAL: Verify the password was actually saved by testing login
      // Sign out first to clear any cached sessions
      await supabase.auth.signOut();
      
      // Attempt to sign in with the new credentials to verify they work
      const { data: verifyData, error: verifyError } = await supabase.auth.signInWithPassword({
        email: userEmail.toLowerCase(),
        password: newPassword
      });

      if (verifyError || !verifyData.user) {
        console.error("Password reset: Password verification failed:", verifyError);
        toast.error('Password update failed - unable to verify credentials. Please try again or contact support.');
        return;
      }

      console.log("Password reset: Password verification successful, proceeding with member linking...");
      
      // Now try to link this user to their organization member record
      try {
        console.log("Password reset: Attempting to link member account...");
        
        const { data: linkResult, error: linkError } = await supabase.rpc('update_member_auth_user_id', {
          member_email: userEmail.toLowerCase(),
          new_auth_user_id: verifyData.user.id
        });

        if (linkError) {
          console.error("Password reset: RPC linking failed:", linkError);
          
          // Fallback: Try direct update
          console.log("Password reset: Trying direct member update...");
          const { error: updateError } = await supabase
            .from('organization_members')
            .update({ 
              auth_user_id: verifyData.user.id,
              invitation_status: 'active',
              last_login_at: new Date().toISOString()
            })
            .eq('email', userEmail.toLowerCase())
            .is('auth_user_id', null);

          if (updateError) {
            console.error("Password reset: Direct update also failed:", updateError);
            toast.error('Password updated but account linking failed. Please try logging in - if that fails, contact support with error code: RESET_LINK_FAILED');
            navigate("/auth");
            return;
          }
          
          console.log("Password reset: Direct update successful");
        } else if (linkResult) {
          console.log("Password reset: RPC successful, member linked");
        } else {
          console.log("Password reset: No member found to link - user may not be an organization member");
        }

        toast.success('Password updated successfully! You can now log in with your new password.');
        
        // Sign out and redirect to login for a clean start
        await supabase.auth.signOut();
        sessionStorage.removeItem('organization_member_id');
        navigate("/auth");
        
      } catch (linkingError: any) {
        console.error("Password reset: Member linking error:", linkingError);
        toast.error('Password updated but account setup incomplete. Please try logging in - if that fails, contact support.');
        navigate("/auth");
      }
      
    } catch (error: any) {
      console.error("Password reset: Fatal error:", error);
      toast.error(`Password reset failed: ${error.message || 'Unknown error'}. Please contact support if this continues.`);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field: 'new' | 'confirm') => {
    if (field === 'new') {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F4F6F8] to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-[#E6EEF5] shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-[#F4F6F8] rounded-full flex items-center justify-center">
              <KeyRound className="w-8 h-8 text-[#2F80ED]" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-[#1A237E]">Update Password</CardTitle>
          <CardDescription className="text-[#2F80ED]">
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div className="space-y-2">
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading || !isValidRecovery}
                  className="border-[#E6EEF5] focus:border-[#2F80ED] focus:ring-[#2F80ED] pr-10"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2F80ED] hover:text-[#1A237E] transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading || !isValidRecovery}
                  className="border-[#E6EEF5] focus:border-[#2F80ED] focus:ring-[#2F80ED] pr-10"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2F80ED] hover:text-[#1A237E] transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Button
                type="submit"
                className="w-full bg-[#1A237E] hover:bg-[#673AB7] text-white transition-colors"
                disabled={loading || !isValidRecovery}
              >
                {loading ? "Updating..." : "Update Password"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full border-[#2F80ED] text-[#2F80ED] hover:bg-[#2F80ED] hover:text-white transition-colors"
                onClick={() => navigate('/auth')}
                disabled={loading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
